import dayjs, { Dayjs } from "dayjs";
import {
  CreditDebitNoteBuyerRequest,
  CreditDebitNoteCreateRequest,
  CreditDebitNoteItemRequest,
  CreditDebitNoteOriginalBill,
  CreditDebitNoteOriginalLine,
  ManualNoteTaxType,
  NoteEtaxReasonCode,
  NoteReasonType,
  NoteSettlementMethod,
  NoteType,
} from "../../types/credit-debit-note.type";
import { TransactionDetailWithItems } from "../../types/sale.type";
import { formatNumber } from "../../utils";
import { roundHalfUp } from "../../utils/money";
import { CustomerOption } from "../SpecialOrder/CustomerPicker";
import { API_DATE_FORMAT } from "../SpecialOrder/data";
import {
  CREDIT_ETAX_REASON_CODES,
  DEBIT_ETAX_REASON_CODES,
  DEBIT_REASON_TYPE,
  QTY_CREDIT_REASON_TYPES,
} from "./constants";
import { NoteLineInput, computeNoteTotals } from "./totals";

/**
 * The create form's state and the pure rules over it: what the draft must hold before it is
 * sent, and how it maps onto `POST /credit-debit-notes`. Kept apart from the components so
 * the mapping can be read (and tested) in one place.
 */

/** Linked to a bill in the system, or keyed in by hand. */
export type InvoiceSource = "LINKED" | "MANUAL";

/** A manually keyed note names a customer in the system, or types the buyer. */
export type BuyerSource = "CUSTOMER" | "TYPED";

/** A line of a note without a bill: a catalogue product and its figures. */
export interface NoteLineDraft {
  key: string;
  /** The catalogue product picked; null until one is picked. */
  productUuid: string | null;
  productCode: string;
  productName: string;
  qty: string;
  unitPrice: string;
  discountAmount: string;
}

/** A ticked line of the linked bill: how much of it the note takes back or re-prices. */
export interface BillLineDraft {
  /** The bill line, `original_item_sequence`. */
  sequence: number;
  qty: string;
  /** Keyed on a re-pricing line only; a RETURN / DAMAGED line is priced at the bill's net price. */
  unitPrice: string;
}

export interface BuyerDraft {
  name: string;
  address: string;
  taxId: string;
  branch: string;
  contact: string;
  phone: string;
}

export interface NoteDraft {
  noteType: NoteType;
  noteDate: Dayjs | null;
  /** The branch head office / master picked; unused for a branch user. */
  branchUuid: string;
  invoiceSource: InvoiceSource;
  invoice: TransactionDetailWithItems | null;
  /** The linked bill's lines and caps; read together with `invoice`. */
  originalBill: CreditDebitNoteOriginalBill | null;
  billLines: BillLineDraft[];
  manualInvoiceNo: string;
  manualInvoiceDate: Dayjs | null;
  manualOriginalValue: string;
  manualTaxType: ManualNoteTaxType | "";
  /** Keyed on a note without a bill; a linked note takes it from the bill. */
  settlementMethod: NoteSettlementMethod | "";
  buyerSource: BuyerSource;
  customer: CustomerOption | null;
  buyer: BuyerDraft;
  routeCode: string;
  /** The lines of a note without a bill. */
  lines: NoteLineDraft[];
  /** A credit note's pick; a debit note always re-prices (see noteReasonType). */
  reasonType: NoteReasonType | "";
  etaxReasonCode: NoteEtaxReasonCode | "";
  reason: string;
  remark: string;
}

/** A draft that passed validateDraft, so its e-Tax code is picked. */
export type SendableNoteDraft = NoteDraft & { etaxReasonCode: NoteEtaxReasonCode };

export type NoteFormErrorKey =
  | "branch"
  | "noteDate"
  | "invoice"
  | "manualInvoiceNo"
  | "manualOriginalValue"
  | "manualTaxType"
  | "settlementMethod"
  | "customer"
  | "buyerName"
  | "buyerAddress"
  | "buyerTaxId"
  | "route"
  | "lines"
  | "reasonType"
  | "etaxReasonCode"
  | "reason";

export type NoteFormErrors = Partial<Record<NoteFormErrorKey, string>>;

/** The server's refusals, word for word, so a cap reads the same before and after a save. */
const QTY_EXCEEDS_RETURNABLE =
  "จำนวนเกินจำนวนที่ยังคืนได้ของบิลเดิม (จำนวนขาย ลบที่คืนหรือลดหนี้ไปแล้ว)";
const QTY_EXCEEDS_SOLD = "จำนวนต้องไม่เกินจำนวนที่ขายในบิลเดิม";
const UNIT_PRICE_EXCEEDS_NET = "ราคาต่อหน่วยต้องไม่เกินราคาสุทธิที่ลูกค้าจ่ายในบิลเดิม";
const CREDIT_EXCEEDS_BILL = "ยอดลดหนี้สะสมของบิลนี้เกินมูลค่าบิลรวมใบเพิ่มหนี้";

let lineKeySequence = 0;
const nextLineKey = () => {
  lineKeySequence += 1;
  return `line-${lineKeySequence}`;
};

export const EMPTY_BUYER: BuyerDraft = {
  name: "",
  address: "",
  taxId: "",
  branch: "",
  contact: "",
  phone: "",
};

export const newCatalogueLine = (): NoteLineDraft => ({
  key: nextLineKey(),
  productUuid: null,
  productCode: "",
  productName: "",
  qty: "",
  unitPrice: "",
  discountAmount: "",
});

export const newBillLine = (sequence: number): BillLineDraft => ({
  sequence,
  qty: "",
  unitPrice: "",
});

export const createInitialDraft = (): NoteDraft => ({
  noteType: "CREDIT",
  noteDate: dayjs(),
  branchUuid: "",
  invoiceSource: "LINKED",
  invoice: null,
  originalBill: null,
  billLines: [],
  manualInvoiceNo: "",
  manualInvoiceDate: null,
  manualOriginalValue: "",
  manualTaxType: "",
  settlementMethod: "",
  buyerSource: "CUSTOMER",
  customer: null,
  buyer: EMPTY_BUYER,
  routeCode: "",
  lines: [newCatalogueLine()],
  reasonType: "",
  etaxReasonCode: "",
  reason: "",
  remark: "",
});

/** Digits only — quantities are whole pieces. */
export const sanitizeInteger = (text: string): string => text.replace(/\D/g, "");

/** A Thai tax id: 13 digits, the format the server accepts. */
export const TAX_ID_LENGTH = 13;

/** Digits and one decimal point with at most two decimals — money as it is keyed. */
export const sanitizeMoney = (text: string): string => {
  const [whole, ...fractionParts] = text.replace(/[^\d.]/g, "").split(".");
  if (fractionParts.length === 0) return whole;
  return `${whole}.${fractionParts.join("").slice(0, 2)}`;
};

const toNumber = (text: string): number => (text.trim() === "" ? 0 : Number(text));

export const toLineInput = (line: NoteLineDraft): NoteLineInput => ({
  qty: toNumber(line.qty),
  unit_price: toNumber(line.unitPrice),
  discount_amount: toNumber(line.discountAmount),
});

/** The reason the note carries: a debit note always re-prices. */
export const noteReasonType = (
  draft: Pick<NoteDraft, "noteType" | "reasonType">,
): NoteReasonType | "" => (draft.noteType === "DEBIT" ? DEBIT_REASON_TYPE : draft.reasonType);

/** The e-Tax codes that fit the note type and reason; none until a credit note's reason is picked. */
export const etaxReasonCodesFor = (
  noteType: NoteType,
  reasonType: NoteReasonType | "",
): NoteEtaxReasonCode[] => {
  if (noteType === "DEBIT") return DEBIT_ETAX_REASON_CODES;
  return reasonType ? CREDIT_ETAX_REASON_CODES[reasonType] : [];
};

/** Drops the picked e-Tax code once a change of note type or reason leaves it out of the list. */
export const withFittingEtaxCode = (draft: NoteDraft): NoteDraft => {
  const { etaxReasonCode } = draft;
  if (etaxReasonCode === "") return draft;
  const fits = etaxReasonCodesFor(draft.noteType, noteReasonType(draft)).includes(etaxReasonCode);
  return fits ? draft : { ...draft, etaxReasonCode: "" };
};

/** A RETURN / DAMAGED credit takes qty back from a bill line, so the line caps it at what is returnable. */
const isQtyCredit = (noteType: NoteType, reasonType: NoteReasonType | ""): boolean =>
  noteType === "CREDIT" && reasonType !== "" && QTY_CREDIT_REASON_TYPES.includes(reasonType);

/** A re-pricing line keys its own unit price; a RETURN / DAMAGED line uses the bill's net price. */
export const isUnitPriceKeyed = (noteType: NoteType, reasonType: NoteReasonType | ""): boolean =>
  noteType === "DEBIT" || reasonType === "PRICE";

/** Most a bill line takes on this note: what is still returnable, or for a re-pricing what was sold. */
export const billLineMaxQty = (
  billLine: CreditDebitNoteOriginalLine,
  noteType: NoteType,
  reasonType: NoteReasonType | "",
): number => (isQtyCredit(noteType, reasonType) ? billLine.returnable_qty : billLine.sold_qty);

const findBillLine = (
  bill: CreditDebitNoteOriginalBill | null,
  sequence: number,
): CreditDebitNoteOriginalLine | undefined =>
  bill?.lines.find((billLine) => billLine.original_item_sequence === sequence);

export const billLineInput = (
  line: BillLineDraft,
  billLine: CreditDebitNoteOriginalLine,
  isPriceKeyed: boolean,
): NoteLineInput => ({
  qty: toNumber(line.qty),
  unit_price: isPriceKeyed ? toNumber(line.unitPrice) : billLine.net_unit_price,
  discount_amount: 0,
});

/** The first cap a ticked bill line breaks, in the server's words; undefined when it keeps them all. */
export const billLineError = (
  line: BillLineDraft,
  billLine: CreditDebitNoteOriginalLine,
  noteType: NoteType,
  reasonType: NoteReasonType | "",
): string | undefined => {
  const isPriceKeyed = isUnitPriceKeyed(noteType, reasonType);
  const { qty, unit_price } = billLineInput(line, billLine, isPriceKeyed);
  if (qty <= 0) return "กรุณากรอกจำนวนให้ครบทุกรายการที่เลือก";
  if (qty > billLineMaxQty(billLine, noteType, reasonType)) {
    return isQtyCredit(noteType, reasonType) ? QTY_EXCEEDS_RETURNABLE : QTY_EXCEEDS_SOLD;
  }
  if (!isPriceKeyed) return undefined;
  if (unit_price <= 0) return "กรุณากรอกราคาต่อหน่วยให้ครบทุกรายการที่เลือก";
  const isCreditAboveNet = noteType === "CREDIT" && unit_price > billLine.net_unit_price;
  return isCreditAboveNet ? UNIT_PRICE_EXCEEDS_NET : undefined;
};

/** The ticked bill lines paired with the bill's own line; a line the bill no longer lists is left out. */
const linkedLinePairs = (draft: NoteDraft) =>
  draft.billLines.flatMap((line) => {
    const billLine = findBillLine(draft.originalBill, line.sequence);
    return billLine ? [{ line, billLine }] : [];
  });

/** The figures the totals preview prices: the ticked bill lines, or the keyed lines. */
const draftLineInputs = (draft: NoteDraft): NoteLineInput[] => {
  if (draft.invoiceSource === "MANUAL") return draft.lines.map(toLineInput);
  const isPriceKeyed = isUnitPriceKeyed(draft.noteType, noteReasonType(draft));
  return linkedLinePairs(draft).map(({ line, billLine }) =>
    billLineInput(line, billLine, isPriceKeyed),
  );
};

/** How the lines are priced: from the linked bill, else as keyed in. */
export const draftTaxType = (draft: NoteDraft): string | null =>
  draft.invoiceSource === "LINKED" ? draft.invoice?.tax_type ?? null : draft.manualTaxType || null;

/**
 * The original invoice's ex-VAT value, as the server reads it: for a linked bill its grand total
 * less its VAT, each to the satang (the tax base its e-Tax invoice declares); otherwise the value
 * keyed in. null while neither is known.
 */
export const draftOriginalValue = (draft: NoteDraft): number | null => {
  if (draft.invoiceSource === "LINKED") {
    if (!draft.invoice) return null;
    const grandTotal = roundHalfUp(Number(draft.invoice.grand_total ?? 0));
    const taxAmount = roundHalfUp(Number(draft.invoice.tax_amount ?? 0));
    return roundHalfUp(grandTotal - taxAmount);
  }
  return draft.manualOriginalValue.trim() === "" ? null : Number(draft.manualOriginalValue);
};

export const draftTotals = (draft: NoteDraft) =>
  computeNoteTotals(
    draftLineInputs(draft),
    draftTaxType(draft),
    draft.noteType,
    draftOriginalValue(draft),
  );

const catalogueLineError = (line: NoteLineDraft): string | undefined => {
  const { qty, unit_price, discount_amount } = toLineInput(line);
  if (!line.productCode) return "กรุณาเลือกสินค้าให้ครบทุกบรรทัด";
  if (qty <= 0) return "กรุณากรอกจำนวนให้ครบทุกบรรทัด";
  if (unit_price <= 0) return "กรุณากรอกราคาต่อหน่วยให้ครบทุกบรรทัด";
  if (discount_amount > qty * unit_price) return "ส่วนลดต้องไม่เกินจำนวนเงินของบรรทัด";
  return undefined;
};

/**
 * A linked note's lines against the bill's caps, as the server checks them: each ticked line
 * within its qty and unit-price caps, then a credit note within what the bill can still be
 * credited (the bill's value plus its live debit notes, less its live credit notes).
 */
const linkedLinesError = (draft: NoteDraft, bill: CreditDebitNoteOriginalBill): string | undefined => {
  if (draft.billLines.length === 0) return "กรุณาเลือกรายการจากบิลเดิมอย่างน้อย 1 รายการ";
  const reasonType = noteReasonType(draft);
  const firstLineError = linkedLinePairs(draft)
    .map(({ line, billLine }) => billLineError(line, billLine, draft.noteType, reasonType))
    .find(Boolean);
  if (firstLineError) return firstLineError;
  const isOverCredit =
    draft.noteType === "CREDIT" && draftTotals(draft).difference > bill.remaining_creditable_value;
  return isOverCredit
    ? `${CREDIT_EXCEEDS_BILL} (ลดหนี้ได้อีก ${formatNumber(bill.remaining_creditable_value)} บาท ก่อน VAT)`
    : undefined;
};

/** A typed buyer needs a name and an address; a tax id, when given, is 13 digits. */
const typedBuyerErrors = (buyer: BuyerDraft): NoteFormErrors => {
  const errors: NoteFormErrors = {};
  if (!buyer.name.trim()) errors.buyerName = "กรุณากรอกชื่อผู้ซื้อ";
  if (!buyer.address.trim()) errors.buyerAddress = "กรุณากรอกที่อยู่ผู้ซื้อ";
  const taxId = buyer.taxId.trim();
  if (taxId && taxId.length !== TAX_ID_LENGTH)
    errors.buyerTaxId = `เลขประจำตัวผู้เสียภาษีต้องเป็นตัวเลข ${TAX_ID_LENGTH} หลัก`;
  return errors;
};

/** The invoice, buyer, settlement and lines of a note keyed without a bill. */
const manualErrors = (draft: NoteDraft): NoteFormErrors => {
  const errors: NoteFormErrors = {};
  if (!draft.manualInvoiceNo.trim()) errors.manualInvoiceNo = "กรุณากรอกเลขที่ใบกำกับภาษีเดิม";
  if (draft.manualOriginalValue.trim() === "")
    errors.manualOriginalValue = "กรุณากรอกมูลค่าตามใบกำกับภาษีเดิม";
  if (!draft.manualTaxType) errors.manualTaxType = "กรุณาเลือกประเภทราคา";
  if (!draft.settlementMethod) errors.settlementMethod = "กรุณาเลือกการชำระเงิน";
  if (draft.buyerSource === "CUSTOMER" && !draft.customer) errors.customer = "กรุณาเลือกลูกค้า";
  if (draft.buyerSource === "TYPED") Object.assign(errors, typedBuyerErrors(draft.buyer));
  if (draft.lines.length === 0) errors.lines = "กรุณาเพิ่มสินค้าอย่างน้อย 1 รายการ";
  else {
    const firstLineError = draft.lines.map(catalogueLineError).find(Boolean);
    if (firstLineError) errors.lines = firstLineError;
  }
  return errors;
};

const linkedErrors = (draft: NoteDraft): NoteFormErrors => {
  if (!draft.invoice || !draft.originalBill) return { invoice: "กรุณาเลือกใบกำกับภาษีเดิม" };
  const linesError = linkedLinesError(draft, draft.originalBill);
  return linesError ? { lines: linesError } : {};
};

export function validateDraft(draft: NoteDraft, canSelectBranch: boolean): NoteFormErrors {
  const errors: NoteFormErrors = {};
  if (canSelectBranch && !draft.branchUuid) errors.branch = "กรุณาเลือกสาขา";
  if (!draft.noteDate?.isValid()) errors.noteDate = "กรุณาเลือกวันที่เอกสาร";
  else if (draft.noteDate.isAfter(dayjs(), "day")) errors.noteDate = "เลือกวันที่ล่วงหน้าไม่ได้";

  Object.assign(errors, draft.invoiceSource === "LINKED" ? linkedErrors(draft) : manualErrors(draft));

  if (!draft.routeCode) errors.route = "กรุณาเลือกสายส่ง";
  if (draft.noteType === "CREDIT" && !draft.reasonType) errors.reasonType = "กรุณาเลือกประเภทเหตุผล";
  if (!draft.etaxReasonCode) errors.etaxReasonCode = "กรุณาเลือกรหัสเหตุผล e-Tax";
  if (!draft.reason.trim()) errors.reason = "กรุณาระบุสาเหตุ";
  return errors;
}

export const isSendableDraft = (
  draft: NoteDraft,
  canSelectBranch: boolean,
): draft is SendableNoteDraft =>
  draft.etaxReasonCode !== "" && Object.keys(validateDraft(draft, canSelectBranch)).length === 0;

/** Optional text: trimmed, and left out of the body when blank. */
const optionalText = (text: string): string | undefined => text.trim() || undefined;

const typedBuyer = (buyer: BuyerDraft): CreditDebitNoteBuyerRequest => ({
  buyer_name: buyer.name.trim(),
  buyer_address: optionalText(buyer.address),
  buyer_tax_id: optionalText(buyer.taxId),
  buyer_branch: optionalText(buyer.branch),
  buyer_contact: optionalText(buyer.contact),
  buyer_phone: optionalText(buyer.phone),
});

/** The part of the body that says which invoice the note corrects, who the buyer is and how it settles. */
type InvoiceFields = Omit<
  CreditDebitNoteCreateRequest,
  | "note_type"
  | "note_date"
  | "branch_code"
  | "route_code"
  | "reason_type"
  | "etax_reason_code"
  | "reason"
  | "remark"
  | "items"
>;

const invoiceFields = (draft: NoteDraft): InvoiceFields => {
  if (draft.invoiceSource === "LINKED" && draft.invoice) {
    return {
      original_transaction_uuid: draft.invoice.uuid,
      original_invoice_no: draft.invoice.transaction_no ?? "",
    };
  }
  const buyer =
    draft.buyerSource === "CUSTOMER"
      ? { customer_code: draft.customer?.customer_code }
      : typedBuyer(draft.buyer);
  return {
    original_invoice_no: draft.manualInvoiceNo.trim(),
    original_invoice_date: draft.manualInvoiceDate?.isValid()
      ? draft.manualInvoiceDate.format(API_DATE_FORMAT)
      : undefined,
    original_value: Number(draft.manualOriginalValue),
    tax_type: draft.manualTaxType || undefined,
    settlement_method: draft.settlementMethod || undefined,
    ...buyer,
  };
};

/** A linked note names each bill line it corrects; a note without a bill names each product. */
const itemRequests = (draft: NoteDraft): CreditDebitNoteItemRequest[] => {
  if (draft.invoiceSource === "LINKED") {
    const isPriceKeyed = isUnitPriceKeyed(draft.noteType, noteReasonType(draft));
    return linkedLinePairs(draft).map(({ line, billLine }) => {
      const { qty, unit_price } = billLineInput(line, billLine, isPriceKeyed);
      return { original_item_sequence: line.sequence, qty, unit_price };
    });
  }
  return draft.lines.map((line) => {
    const { qty, unit_price, discount_amount } = toLineInput(line);
    return {
      product_code: line.productCode,
      qty,
      unit_price,
      discount_amount: discount_amount > 0 ? discount_amount : undefined,
    };
  });
};

/**
 * The request body for a draft that passed `validateDraft`. Linked to a bill, the server
 * derives the tax type, the original value, the invoice date, the buyer and the settlement from
 * it, so none of them is sent; `branchCode` is sent only by head office / master. A debit note
 * always re-prices, so it leaves the reason type out.
 */
export const buildCreateRequest = (
  draft: SendableNoteDraft,
  branchCode: string | undefined,
): CreditDebitNoteCreateRequest => ({
  note_type: draft.noteType,
  note_date: draft.noteDate?.format(API_DATE_FORMAT) ?? "",
  branch_code: branchCode,
  ...invoiceFields(draft),
  route_code: draft.routeCode,
  reason_type: draft.noteType === "CREDIT" ? draft.reasonType || undefined : undefined,
  etax_reason_code: draft.etaxReasonCode,
  reason: draft.reason.trim(),
  remark: optionalText(draft.remark),
  items: itemRequests(draft),
});
